import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';
import {AxiosError, AxiosResponse} from 'axios';
import {setEntities, UIEntitiesRef, updateAllEntities, upsertEntitiesById} from '@ngneat/elf-entities';
import {setPage, updatePaginationData} from '@ngneat/elf-pagination';

import APIAxios, {APIRoutes} from '@api/axios.api';
import SnackError from '@utils/error.utils';
import {
  IssueDetails,
  IssueKind,
  IssuePagination,
  IssuesFilters,
  IssuesGroupedAction,
  IssueStatusHistory,
  ManageIssue
} from '@store/operations/issues/issues.model';
import {issuesStore} from '@store/operations/issues/issues.store';
import {getIssuesDataSource} from '@store/operations/issues/issues.requests';
import {Pagination} from '@store/common/pagination.model';
import {combineDateAndHours} from '@utils/dayjs.utils';
import {GridSortItem} from '@mui/x-data-grid-pro';
import {issuesQuery} from '@store/operations/issues/issues.query';
import {TableFilter} from '@store/common/tableFilter.model';
import {extractFilenameFromResponse} from '@utils/response.utils';
import {filesService} from '@store/files';

export class IssuesService {
  store = issuesStore;

  setSort = (sort: GridSortItem) => this.store.update((state) => ({...state, sort}));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = () => this.store.update((state) => ({
    ...state,
    filters: {search: '', tableFilters: IssuesFilters.initTableFilters()},
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  getIssues = (): Observable<IssuePagination> => {
    const {
      filters: {search, issueStatus, from: fromDate, to: toDate, tableFilters},
      sort,
      tablePagination
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETIssues(),
      params: {
        ...requestFilters,
        issueStatus: issueStatus || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<IssuePagination>) => {
          return response.data;
        }),
        tap((issuePagination) => {
          const paginationData = Pagination.toPaginationData(issuePagination, tablePagination.page);

          this.store.update(
            setEntities(issuePagination.data),
            upsertEntitiesById(issuePagination.data.map((issue) => issue.id), {
              updater: (issueUi) => issueUi,
              creator: (issueId) => ({id: issueId, selected: false}),
              ref: UIEntitiesRef,
            }),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, issuePagination.data.map((execution) => execution.id)),
            getIssuesDataSource.setSuccess(),
          );
        }),
        getIssuesDataSource.trackRequestStatus(),
      );
  };

  getIssuesCSV = () => {
    const {filters: {search, issueStatus, from: fromDate, to: toDate, tableFilters}, sort} = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETIssuesCSV(),
      params: {
        ...requestFilters,
        disablePaginate: true,
        issueStatus: issueStatus || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        searchString: search || undefined,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
      responseType: 'blob',
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap((response: AxiosResponse) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || 'export.csv');
        }),
      );
  };

  getIssueById = (issueId: number | string): Observable<ManageIssue> => {
    return from(APIAxios(APIRoutes.GETIssueById(issueId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<IssueDetails>) => {
          return IssueDetails.toManageIssue(response.data);
        }),
      );
  };

  getIssueHistory = (issueId: number | string): Observable<IssueStatusHistory[]> => {
    return from(APIAxios(APIRoutes.GETIssueHistory(issueId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<IssueStatusHistory[]>) => {
          return response.data;
        }),
      );
  };

  getIssueKinds = (): Observable<IssueKind[]> => {
    return from(APIAxios(APIRoutes.GETIssueKinds()))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<IssueKind[]>) => {
          return response.data;
        }),
      );
  };

  createIssue = (issue: ManageIssue, executionId: number | string): Observable<IssueDetails> => {
    return from(APIAxios({
      ...APIRoutes.POSTExecutionIssue(executionId),
      data: {
        documents: (issue.documents || []).map((d) => d.id),
        issueKind: issue.issueKind?.id,
        reportedAt: issue.reportedAt && issue.dateHour ? combineDateAndHours(issue.reportedAt, issue.dateHour).toISOString() : undefined,
        description: issue.description,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<IssueDetails>) => {
          return response.data;
        }),
        switchMap((issue) => this.getIssues().pipe(map(() => issue)))
      );
  };

  createIssueWithoutExecution = (issue: ManageIssue): Observable<IssueDetails> => {
    return from(APIAxios({
      ...APIRoutes.POSTIssue(),
      data: {
        documents: (issue.documents || []).map((d) => d.id),
        issueKind: issue.issueKind?.id,
        clientOrganisationUnitId: issue.clientOrganisationUnitId,
        supplierOrganisationUnitId: issue.supplierOrganisationUnitId,
        reportedAt: issue.reportedAt && issue.dateHour ? combineDateAndHours(issue.reportedAt, issue.dateHour).toISOString() : undefined,
        description: issue.description,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<IssueDetails>) => {
          return response.data;
        }),
        switchMap((issue) => this.getIssues().pipe(map(() => issue)))
      );
  };


  updateIssue = (issueId: number | string, issue: ManageIssue): Observable<ManageIssue> => {
    return from(APIAxios({
      ...APIRoutes.PATCHIssueById(issueId),
      data: {
        description: issue.description,
        status: issue.status,
        resolution: issue.resolution,
        clientOrganisationUnitId: issue.clientOrganisationUnitId,
        supplierOrganisationUnitId: issue.supplierOrganisationUnitId,
        documents: issue.documents?.map((d) => d.id) || [],
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<IssueDetails>) => {
          return IssueDetails.toManageIssue(response.data);
        }),
        switchMap((issue) => this.getIssues().pipe(map(() => issue)))
      );
  };

  updateIssueInList = (issueId: number | string, issue: Partial<ManageIssue>): Observable<IssuePagination> => {
    return from(APIAxios({
      ...APIRoutes.PATCHIssueById(issueId),
      data: {status: issue.status},
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getIssues()),
      );
  };

  setSelectedIssues = (issueIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (issueUi) => ({
          ...issueUi,
          selected: issueIds.includes(issueUi.id)
        }),
        {ref: UIEntitiesRef},
      ),
    );
  };

  issuesGroupedAction = (groupedAction: IssuesGroupedAction): Observable<number[]> => {
    const ids = issuesQuery.selectedIssues;

    return from(APIAxios({
      ...APIRoutes.PATCHIssuesGroupedAction(),
      data: {ids, ...groupedAction},
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getIssues().pipe(map(() => ids))),
        tap(() => this.setSelectedIssues([])),
      );
  };
}

export const issuesService = new IssuesService();
